import React from 'react'
import T from 'prop-types'
import {
  Card,
  Step,
  StepConnector,
  StepButton,
  StepLabel,
  Stepper as MuiStepper,
  stepConnectorClasses,
  stepLabelClasses,
  stepIconClasses,
  stepButtonClasses,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import {
  SESSIONS_STEP_ACTIVE,
  SESSIONS_STEP_OPEN,
  SESSIONS_STEP_COMPLETED,
  SESSIONS_STEP_COMPLETED_WITH_ERRORS,
  SESSIONS_STEP_DISABLED,
} from 'components/organisms/Sessions/sessionsLib'

import DotCell from './DotCell'
import Tooltip from './Tooltip'

const StyledStep = styled(Step)(({ theme }) => ({
  marginLeft: theme.spacing(1.5),
  marginRight: theme.spacing(1.5),
  padding: theme.spacing(0.5),
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  '&.active': {
    backgroundColor: theme.custom.stepHover,
    borderRadius: theme.shape.borderRadius,
  },
  [`& .${stepLabelClasses.label}`]: {
    color: theme.palette.text.primary,
    [`&.${stepLabelClasses.completed}`]: {
      fontWeight: 'normal',
    },
  },
  [`&.${SESSIONS_STEP_COMPLETED}`]: {
    [`& .${stepIconClasses.root}`]: {
      color: theme.palette.success.main,
    },
  },
  [`&.${SESSIONS_STEP_OPEN}`]: {
    [`& .${stepIconClasses.root}`]: {
      color: theme.palette.warning.main,
    },
  },
  [`&.${SESSIONS_STEP_COMPLETED_WITH_ERRORS}`]: {
    [`& .${stepIconClasses.root}`]: {
      color: theme.palette.error.main,
    },
  },
  [`&.${SESSIONS_STEP_DISABLED}`]: {
    [`& .${stepIconClasses.root}`]: {
      color: theme.palette.action.disabledBackground,
    },
    [`& .${stepIconClasses.text}`]: {
      fill: theme.palette.text.primary,
    },
    [`& .${stepLabelClasses.label}`]: {
      color: theme.palette.action.disabled,
    },
    [`& .${stepButtonClasses.root}.Mui-disabled`]: {
      pointerEvents: 'initial',
    },
  },
}))

const StyledStepConnector = styled(StepConnector)(({ theme }) => ({
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.action.disabled,
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.text.primary,
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.text.primary,
  },
}))

export default function Stepper({
  steps,
  activeStep,
  stepperProps,
  handleStep = (index) => {},
}) {
  const { t } = useTranslation()

  return (
    <Card>
      <MuiStepper
        nonLinear
        activeStep={activeStep}
        connector={<StyledStepConnector />}
        {...stepperProps}
        sx={{ p: 1, ...stepperProps?.sx }}
      >
        {steps.map((step, index) => {
          return (
            <StyledStep
              key={step.label}
              className={classNames(step.state, {
                [SESSIONS_STEP_ACTIVE]: index === activeStep,
              })}
              disabled={step.state === SESSIONS_STEP_DISABLED}
              completed={index < activeStep}
            >
              <StepButton
                data-testid="session-step-button"
                icon={<div />}
                onClick={() => handleStep(index)}
                disabled={step.state === SESSIONS_STEP_DISABLED}
              >
                <Tooltip
                  title={
                    <DotCell
                      label={t(`sessions.stepper.state.${step.state}`)}
                      color={
                        step.state === SESSIONS_STEP_OPEN
                          ? 'warning'
                          : step.state === SESSIONS_STEP_COMPLETED
                          ? 'success'
                          : step.state === SESSIONS_STEP_COMPLETED_WITH_ERRORS
                          ? 'error'
                          : 'textSecondary'
                      }
                    />
                  }
                  followCursor
                  placement="bottom-start"
                >
                  <StepLabel
                    StepIconProps={{
                      icon: index + 1,
                      completed: false,
                      className: step.state,
                    }}
                  >
                    {step.label}
                  </StepLabel>
                </Tooltip>
              </StepButton>
            </StyledStep>
          )
        })}
      </MuiStepper>
    </Card>
  )
}
Stepper.propTypes = {
  steps: T.arrayOf(
    T.shape({
      label: T.string,
      state: T.string,
    })
  ),
  activeStep: T.number.isRequired,
  stepperProps: T.object,
  handleStep: T.func,
}
